import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import Select from '../../../../components/Select'

import { useDebouncedSearch } from '../../../../hooks/useDebouncedSearch'

function ManualSearchableSelect({
  name,
  setFieldValue,
  onChange,
  value,
  options,
  isLoading,
  className,
  onSearch,
  searchAction,
  extraSearchParams,
  placeholder = 'Search...',
  noOptionsMessageText,
  ...props
}) {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const onManualSearch = useCallback(
    searchValue => {
      if (onSearch) {
        // regular callback function - to provide more freedom of handling search, also helps to avoid passing
        // extraSearchParams through the components
        onSearch(searchValue)
      } else {
        dispatch(searchAction({ search: searchValue, ...extraSearchParams }))
      }
    },
    [onSearch, dispatch, searchAction, extraSearchParams]
  )

  const [lastSearchValue, setLastSearchValue] = useState('')

  const handleSearchChange = useCallback(
    searchValue => {
      // make BE search every debouncedSearchValue value change
      onManualSearch(searchValue)
      setLastSearchValue(searchValue)
    },
    [onManualSearch]
  )

  const { searchText, clearSearchValue, handleSearchTextChange } = useDebouncedSearch(handleSearchChange)

  const onSearchInputChange = (value, { action }) => {
    if (action === 'input-change') {
      handleSearchTextChange(value)
    }
  }

  const onChangeHandler = useCallback(
    value => {
      onChange(value)

      // when user select value, clear search text
      searchText && clearSearchValue()
    },
    [clearSearchValue, onChange, searchText]
  )

  const noOptionsMessage = useCallback(
    ({ inputValue }) => {
      const wasInputValueFetched = inputValue === lastSearchValue

      if (
        // if input value is empty
        inputValue.trim() === '' ||
        // or if input value was not fetched yet (because of debounce)
        !wasInputValueFetched
      ) {
        return 'Type to search'
      } else {
        return t(noOptionsMessageText || 'No Results')
      }
    },
    [t, noOptionsMessageText, lastSearchValue]
  )

  return (
    <Select
      value={value}
      options={options}
      controlShouldRenderValue={false}
      onChange={onChangeHandler}
      formatValue={false}
      onInputChange={onSearchInputChange}
      onClearInput={clearSearchValue}
      inputValue={searchText}
      isLoading={isLoading}
      backspaceRemovesValue={false}
      showClearInputIcon={!!searchText}
      isMulti
      placeholder={placeholder}
      showIndicator={false}
      noOptionsMessage={noOptionsMessage}
      {...props}
    />
  )
}

ManualSearchableSelect.propTypes = {
  name: PropTypes.string,
  className: PropTypes.string,
  options: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  value: PropTypes.array,
  setFieldValue: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  searchAction: PropTypes.func,
  onSearch: PropTypes.func,
  noOptionsMessageText: PropTypes.string,
  extraSearchParams: PropTypes.object,
  placeholder: PropTypes.string,
  noOptionsMessage: PropTypes.func
}

export default ManualSearchableSelect
